export interface ExpandedLayoutInput {
  /** Notes hold at least one non-whitespace character. */
  hasNotes: boolean
  /** The task has at least one subtask. */
  hasSubtasks: boolean
  /** A catalogue reward is attached. */
  hasReward: boolean
}

export interface ExpandedLayout {
  /** Empty notes read as a compact trigger, filled notes as body copy. */
  notes: 'placeholder' | 'paragraph'
  /**
   * `placeholder-inline` sits in the first row next to the notes trigger,
   * `placeholder-below` moves under the notes paragraph that now owns that
   * row, `list` replaces the placeholder with the checklist block.
   */
  subtasks: 'placeholder-inline' | 'placeholder-below' | 'list'
  /** `inline` flows with the other placeholders, `end` pins to the trailing edge. */
  rewardAlign: 'inline' | 'end'
}

/**
 * Arrangement of the three elements in the expanded task content area —
 * notes, subtasks and reward (Figma 2172-17470 / -15068 / -15474 / -16113).
 *
 * The rule is not "whatever is filled gets its own row". The reward is the
 * anchor: it flows inline with the other placeholders **only** while the card
 * is completely untouched, and pins to the trailing edge the moment any of the
 * three carries content. Notes take the first row as soon as they have text,
 * which is what pushes an empty subtask trigger below them.
 *
 * @example
 * resolveExpandedLayout({ hasNotes: true, hasSubtasks: false, hasReward: true })
 * // → { notes: 'paragraph', subtasks: 'placeholder-below', rewardAlign: 'end' }
 */
export function resolveExpandedLayout({
  hasNotes, hasSubtasks, hasReward,
}: ExpandedLayoutInput): ExpandedLayout {
  const untouched = !hasNotes && !hasSubtasks && !hasReward

  return {
    notes: hasNotes ? 'paragraph' : 'placeholder',
    subtasks: hasSubtasks ? 'list' : hasNotes ? 'placeholder-below' : 'placeholder-inline',
    rewardAlign: untouched ? 'inline' : 'end',
  }
}
